import React, { Fragment, useEffect } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import { Link } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Item } from "../Item/Item";
import { grey, red } from "@mui/material/colors";
import Button from "@mui/material/Button";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import moment from "moment";
import {
  formatPrice,
  getDisplayProduct,
  getSealDetailsDisplay,
  getSellerPayoutBreakdown,
} from "../../utils/utils";
import { OrderUserType } from "../../../constants/order";
import { SERVER_URL } from "../../../constants/global";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import ItemImage from "./ItemImage";
import { useQueryUniqueUsers } from "../../../queries/pharmacy/hooks/useQueryUniqueUsers";
import { useQueryUser } from "../../../queries/user/hooks/useQueryUser";
import axios from "axios";

interface ActionRequiredOrdersProps {
  orders: any;
  setSelectedOrders: (orders: any) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  user?: OrderUserType.buyer | OrderUserType.seller;
}

export const HistoryDialog: React.FunctionComponent<
  ActionRequiredOrdersProps
> = ({ orders, setSelectedOrders, open, setOpen, user }) => {


  const { data, loading } = useQueryUser();
  const { data: pharmacyUsersData, loading: pharmacyUsersLoading } = useQueryUniqueUsers({
    variables: { where: { id: +data?.me?.id } },
    skip: !data,
  });
  const isAdmin = pharmacyUsersData?.findUniqueUser.UserRoles[0]?.Role?.key === "admin";


  const [stripeTransactionID, setStripeTransactionID] = React.useState<string>("");
  const [stripeTransferID, setStripeTransferID] = React.useState<string>("");
  const [stripePayoutID, setStripePayoutID] = React.useState<string>("");
  const [sellerName, setSellerName] = React.useState<string>("");
  const [buyerName, setBuyerName] = React.useState<string>("");
  const [StripePayoutAmount, setStripePayoutAmount] = React.useState<string>("");
  useEffect(() => {
    if (!isAdmin || !orders) return;
    const orderNumber = orders[0]?.orderNumber;
    const api = `${process.env.REACT_APP_API_URL}/v2/order/get/orderByOrderNumber?orderNumber=${orderNumber}`;
    axios.get(api)
      .then((response) => {
        const legalName = response?.data?.data?.[0]?.Seller?.legalName || "Unknown";
        const mail = response?.data?.data?.[0]?.Seller?.email || "Unknown";
        const dataToSet = `${legalName} - ${mail}`;
        const buyerLegalName = response?.data?.data?.[0]?.Buyer?.legalName || "Unknown";
        const buyerMail = response?.data?.data?.[0]?.Buyer?.email || "Unknown";
        const buyerDataToSet = `${buyerLegalName} - ${buyerMail}`;
        setSellerName(dataToSet);
        setBuyerName(buyerDataToSet);
        setStripeTransactionID(response.data.data[0].stripeTransactionId || "pending");
        setStripeTransferID(response.data.data[0].stripeTransferId || "pending");
        setStripePayoutID(response.data.data[0].stripePayoutId || "pending");
        setStripePayoutAmount(response.data.data[0].stripePayoutAmount || "pending");
      })
      .catch((error) => {
        console.error("Error fetching order details:", error);
      });

  }, [isAdmin, orders]);



  const filteredOrders = orders.filter((order: any) => order.status === 1);

  const cutoffDate = new Date("Mon Jan 28 2025 23:59:59 GMT+0400");
  const cutoffDateForGround = new Date("Mon Feb 20 2025 23:59:59 GMT+0400");

  const isNewOrder = React.useMemo(() => {
    return filteredOrders[0]?.createdAt
      ? new Date(filteredOrders[0].createdAt) > cutoffDate
      : false;
  }, [filteredOrders]);

  const getTradeNetFee = (order: any) => {
    const { totalAmount, tradeNetFee } = order;

    return (Number(totalAmount) * Number(tradeNetFee)) / 100;
  };

  const getTotalShippingFee = (orders: any) => {
    const filteredOrders = orders.filter((order: any) => order.status === 1);

    return filteredOrders
      .reduce((totalShippingFee: number, item: any) => {
        const orderDate = new Date(item.createdAt);
        const isNewOrderForGround = orderDate > cutoffDateForGround;
        const isFedexGround = item.ShippingMethod?.key === "fedex-ground";
        const isShippingPaidBySeller = Boolean(item.isShippingPaidBySeller);

        if (user === OrderUserType.seller) {
          // Subtract shipping fee when seller pays shipping
          return (
            totalShippingFee +
            ((isShippingPaidBySeller || (isNewOrderForGround && isFedexGround))
              ? parseFloat(item.shippingFee || "0")
              : 0)
          );
        } else {
          // Buyer sees free shipping when seller pays it
          return (
            totalShippingFee +
            (!(isShippingPaidBySeller || (isNewOrderForGround && isFedexGround))
              ? parseFloat(item.shippingFee || "0")
              : 0)
          );
        }
      }, 0)
      .toFixed(2);
  };

  const getTotalAmount = (orders: any) => {
    const filteredOrders = orders.filter((order: any) => order.status === 1);

    let total = 0;
    filteredOrders.forEach((item: any) => {
      total = total + Number(item.totalAmount);
    });

    return `${total.toFixed(2)}`;
  };

  const getFedexInsurance = (orders: any) => {
    const filteredOrders = orders.filter((order: any) => order.status === 1);

    return filteredOrders[0]?.fedExInsurance || "0";
  };

  const getTotal = (orders: any) => {
    if (user === OrderUserType.seller) {
      const breakdown = getSellerPayoutBreakdown(
        orders.filter((order: any) => order.status === 1)
      );
      if (!breakdown) return "0.00";
      return breakdown.creditTotal.toFixed(2);
    }

    const filteredOrders = orders.filter((order: any) => order.status === 1);
    let total = 0;
    filteredOrders.forEach((item: any) => {
      total += Number(item.totalAmount);
    });
    return total.toFixed(2);
  };

  const sellerPayoutBreakdown =
    user === OrderUserType.seller
      ? getSellerPayoutBreakdown(filteredOrders)
      : null;

  const getPackTypeDetails = (type?: number, packaging?: string | number) => {
    if (type === 2) {
      return packaging !== undefined && packaging !== null && `${packaging}` !== ""
        ? `Partial (count: ${packaging})`
        : "Partial";
    }

    return "Full";
  };

  const renderPackageCondition = (
    packageCondition: number,
    sealDetails?: string,
    type?: number,
    packaging?: string | number
  ) => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <Box sx={{ fontSize: 12, fontWeight: 400 }}>
          {packageCondition === 1 ? "Sealed" : "Non-Sealed"}
        </Box>
        {sealDetails && (
          <Box sx={{ fontSize: 11, color: "text.secondary", mt: 0.5, wordBreak: "break-word" }}>
            {getSealDetailsDisplay(sealDetails)}
          </Box>
        )}
        <Box sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
          {getPackTypeDetails(type, packaging)}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={() => {
        setOpen(false);
        setSelectedOrders(null);
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>Order Details</Box>
        <CloseIcon
          sx={{ cursor: "pointer" }}
          onClick={() => {
            setOpen(false);
            setSelectedOrders(null);
          }}
        />
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>

        {orders.map((order: any) => {
          const displayProduct = getDisplayProduct(order);
          return (
          <Fragment key={order.id}>
            <Grid container spacing={0} sx={{ p: 2 }}>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="Drug name"
                  value={displayProduct.drugName}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="Manufacturer"
                  value={displayProduct.labelerName}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="Form"
                  value={displayProduct.form}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="Strength"
                  value={displayProduct.strength}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="Package Condition"
                  value=""
                  column
                >
                  {renderPackageCondition(
                    displayProduct.packageCondition,
                    displayProduct.sealDetails,
                    displayProduct.type,
                    displayProduct.packaging
                  )}
                </Item>
              </Grid>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="NDC"
                  value={displayProduct.ndc}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="Lot"
                  value={displayProduct.lotNumber}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="Exp. Date"
                  value={moment(displayProduct.expDate).format("MM/YYYY")}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3}>
                <Item
                  fontSize={12}
                  label="Price / Unit"
                  value={`$${Number(displayProduct.price).toFixed(
                    2
                  )} / $${Number(displayProduct.unitPrice).toFixed(2)}`}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="Quantity"
                  value={order.quantity?.toString() || "0"}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3} sx={{ mb: 1 }}>
                <Item
                  fontSize={12}
                  label="Subtotal"
                  value={`$${Number(order.totalAmount || 0).toFixed(2)}`}
                  column
                />
              </Grid>
              <Grid item xs={12} sm={6} xl={3}>
                <ItemImage ndc={displayProduct.ndc} />
              </Grid>
            </Grid>
            <Divider />
          </Fragment>
          );
        })}
      </DialogContent>
      <Divider />
      {orders[0].status === 1 && (

        <DialogActions
          sx={{ p: 2, backgroundColor: grey[100], alignItems: "end" }}
        >
          {isAdmin && (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ mt: 2, maxWidth: 460, border: "1px solid", borderColor: "divider" }}
            >
              <Table size="small" aria-label="admin transaction details">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: "40%", fontSize: 12, color: "text.secondary" }}>Order Number</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box>{orders?.[0]?.orderNumber || "Unknown"}</Box>
                        <Tooltip title="Copy order number">
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(orders?.[0]?.orderNumber || "")}
                          >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>Seller</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{sellerName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>Buyer</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{buyerName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>Transaction ID</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <a
                        href={`https://dashboard.stripe.com/payments/${stripeTransactionID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {stripeTransactionID}
                      </a>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>Transfer ID</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <a
                        href={`https://dashboard.stripe.com/connect/transfers/${stripeTransferID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {stripeTransferID}
                      </a>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>Stripe Payout ID</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{stripePayoutID}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>Stripe Payout Amount</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{StripePayoutAmount}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Grid
            container
            sx={{ height: "100%", display: "flex", justifyContent: "start" }}
          >
            {user === OrderUserType.seller &&
              orders[0].shippingStatus === 5 &&
              filteredOrders[0]?.stripeTransactionId && (
                <Link
                  href={`${SERVER_URL}/pdf/invoice/seller/${filteredOrders[0]?.stripeTransactionId}`}
                  target="_blank"
                >
                  <Button
                    variant="contained"
                    color="success"
                    onClick={(event: any) => event.stopPropagation()}
                  >
                    <ReceiptOutlinedIcon
                      sx={{ mr: 2, cursor: "pointer", color: "#FFFFFF" }}
                    />
                    View Invoice
                  </Button>
                </Link>
              )}
            {user === OrderUserType.buyer &&
              orders[0].shippingStatus !== 0 &&
              filteredOrders[0]?.stripeTransactionId && (
                <Link
                  href={`${SERVER_URL}/pdf/invoice/buyer/${filteredOrders[0]?.stripeTransactionId}`}
                  target="_blank"
                >
                  <Button
                    variant="contained"
                    color="success"
                    onClick={(event: any) => event.stopPropagation()}
                  >
                    <ReceiptOutlinedIcon
                      sx={{ mr: 2, cursor: "pointer", color: "#FFFFFF" }}
                    />
                    View Invoice
                  </Button>
                </Link>
              )}
          </Grid>
          <Grid
            container
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "end",
              mt: -2,
            }}
          >
            {user === OrderUserType.seller && sellerPayoutBreakdown && (
                <>
                  <Item
                    label="Item(s) Total:"
                    value={`$${sellerPayoutBreakdown.itemsTotal.toFixed(2)}`}
                  />

                  {sellerPayoutBreakdown.shippingFeeDeducted > 0 && (
                    <Item
                      label="Shipping Fee:"
                      value={`-$${sellerPayoutBreakdown.shippingFeeDeducted.toFixed(2)}`}
                    />
                  )}

                  {sellerPayoutBreakdown.transactionFee > 0 && (
                    <Item
                      label="Transaction fee:"
                      value={`-$${sellerPayoutBreakdown.transactionFee.toFixed(2)}`}
                    />
                  )}

                  <Item
                    label={`TradeNetRx fee (${orders[0].tradeNetFee}%)`}
                    value={`-$${sellerPayoutBreakdown.tradeNetFee.toFixed(2)}`}
                  />

                  <Item
                    label="Fedex insurance:"
                    value={
                      sellerPayoutBreakdown.fedExInsurance > 0
                        ? `-$${sellerPayoutBreakdown.fedExInsurance.toFixed(2)}`
                        : "$0.00"
                    }
                  />

                  {sellerPayoutBreakdown.isCardPayment && (
                    <>
                      <Item
                        label="Transaction fee:"
                        value={`-$${sellerPayoutBreakdown.stripeCardFee.toFixed(2)}`}
                      />
                    </>
                  )}

                  <Item
                    label="Credit Total:"
                    value={`$${sellerPayoutBreakdown.creditTotal.toFixed(2)}`}
                  />
                </>
              )}
            {user === OrderUserType.buyer && (
              <>
                <Item
                  label="Item(s) Total:"
                  value={`$${formatPrice(getTotalAmount(orders))}`}
                />

                <Item
                  label="Item(s) Shipping:"
                  value={`$${formatPrice(getTotalShippingFee(orders))}`}
                />

                <Item
                  label="Credit Total:"
                  value={`$${formatPrice(
                    Number(getTotalAmount(orders)) +
                    Number(getTotalShippingFee(orders))
                  )}`}
                />
              </>
            )}
          </Grid>
        </DialogActions>
      )}
    </Dialog>
  );
};
